import React, { useEffect, useState } from "react";
import { Radio, Copy, Mail, Key, Shield, ScrollText, Send, Settings as SettingsIcon, FileCode, Plus, Trash2, CheckCircle2, Power } from "lucide-react";
import api from "../lib/api";
import { Tabs, Pagination, Modal, toast, Spinner } from "../components/ui";
import { format } from "date-fns";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BASE}/api/relay/inbound`;

export default function LeadRelay() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white flex items-center gap-2"><Radio className="w-7 h-7 text-cyan-500" /> Lead Relay</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Public lead intake middleware for external providers.</p>
      </div>
      <Tabs active={tab} onChange={setTab} tabs={[
        { value: "overview", label: "Overview", icon: Radio },
        { value: "keys", label: "API Keys", icon: Key },
        { value: "ips", label: "IP Whitelist", icon: Shield },
        { value: "log", label: "Leads Log", icon: ScrollText },
        { value: "test", label: "Test", icon: Send },
        { value: "settings", label: "Settings", icon: SettingsIcon },
        { value: "integration", label: "API Integration", icon: FileCode },
      ]} />
      {tab === "overview" && <Overview />}
      {tab === "keys" && <ApiKeys />}
      {tab === "ips" && <IpWhitelist />}
      {tab === "log" && <LeadsLog />}
      {tab === "test" && <TestTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "integration" && <Integration />}
    </div>
  );
}

function Card({ label, value, icon: Icon, accent }) {
  return (
    <div className="card p-5 flex items-center justify-between">
      <div><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="text-3xl font-bold font-display text-slate-900 dark:text-white mt-1">{value}</div></div>
      <Icon className={`w-7 h-7 ${accent}`} />
    </div>
  );
}

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/relay/overview").then((r) => setO(r.data)); }, []);
  if (!o) return <Spinner className="w-5 h-5 text-slate-400" />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Card label="Total Leads Received" value={o.total_leads} icon={Mail} accent="text-cyan-500" />
      <Card label="Active API Keys" value={o.active_keys} icon={Key} accent="text-indigo-500" />
      <Card label="Whitelisted IPs" value={o.whitelisted_ips} icon={Shield} accent="text-emerald-500" />
    </div>
  );
}

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState(null);
  const load = () => api.get("/relay/api-keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (!label.trim()) return toast("Enter a label", "error");
    const r = await api.post("/relay/api-keys", { label });
    setCreated(r.data.plaintext); setLabel(""); setOpen(false); load();
  };
  const revoke = async (id) => { await api.post(`/relay/api-keys/${id}/revoke`); load(); };
  const del = async (id) => { if (!window.confirm("Delete key?")) return; await api.delete(`/relay/api-keys/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold font-display text-slate-900 dark:text-white">API Keys</h3>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="relay-gen-key"><Plus className="w-4 h-4" /> Generate Key</button></div>
      {created && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-500/10 p-4">
          <div className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-2">New key — copy it now, it won't be shown again:</div>
          <div className="flex gap-2"><code className="flex-1 font-mono text-sm bg-white dark:bg-slate-900 rounded px-3 py-2 truncate">{created}</code>
            <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(created); toast("Copied"); }}><Copy className="w-4 h-4" /></button></div>
        </div>
      )}
      <div className="card overflow-hidden">
        <table className="w-full text-sm" data-testid="relay-keys-table">
          <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr><th className="p-3 text-left">Label</th><th className="p-3 text-left">Key</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Created</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-slate-100 dark:border-slate-800/50">
                <td className="p-3 font-medium text-slate-800 dark:text-slate-100">{k.label}</td>
                <td className="p-3 font-mono text-slate-400">{k.key_preview}••••••••</td>
                <td className="p-3"><span className={`badge ${k.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" : "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700"}`}>{k.status}</span></td>
                <td className="p-3 text-slate-500">{k.created_at ? format(new Date(k.created_at), "dd/MM/yyyy, HH:mm:ss") : ""}</td>
                <td className="p-3 text-right">
                  {k.status === "active" && <button className="btn-ghost px-2 py-1 text-xs text-amber-600" onClick={() => revoke(k.id)} data-testid={`relay-revoke-${k.id}`}><Power className="w-3.5 h-3.5" /> Revoke</button>}
                  <button className="btn-ghost p-1.5 text-rose-500" onClick={() => del(k.id)}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Generate API Key" testid="relay-key-modal"
        footer={<><button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button><button className="btn-primary" onClick={generate} data-testid="relay-key-create">Generate</button></>}>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Label (provider name)</label>
        <input className="input mt-1" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Recovery Associates" data-testid="relay-key-label" />
      </Modal>
    </div>
  );
}

function IpWhitelist() {
  const [ips, setIps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ip: "", tag: "custom", note: "" });
  const load = () => api.get("/relay/ips").then((r) => setIps(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!form.ip.trim()) return toast("Enter an IP", "error"); await api.post("/relay/ips", form); setForm({ ip: "", tag: "custom", note: "" }); setOpen(false); load(); };
  const del = async (id) => { await api.delete(`/relay/ips/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold font-display text-slate-900 dark:text-white">IP Whitelist</h3>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="relay-add-ip"><Plus className="w-4 h-4" /> Add IP</button></div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm" data-testid="relay-ips-table">
          <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr><th className="p-3 text-left">IP</th><th className="p-3 text-left">Tag</th><th className="p-3 text-left">Note</th><th className="p-3 text-left">Added</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {ips.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 dark:border-slate-800/50">
                <td className="p-3 font-mono text-slate-800 dark:text-slate-100">{i.ip}</td>
                <td className="p-3"><span className="badge bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-800">{i.tag}</span></td>
                <td className="p-3 text-slate-500">{i.note || "-"}</td>
                <td className="p-3 text-slate-500">{i.created_at ? format(new Date(i.created_at), "dd/MM/yyyy, HH:mm:ss") : ""}</td>
                <td className="p-3 text-right"><button className="btn-ghost p-1.5 text-rose-500" onClick={() => del(i.id)} data-testid={`relay-ip-del-${i.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Add IP to Whitelist" testid="relay-ip-modal"
        footer={<><button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button><button className="btn-primary" onClick={add} data-testid="relay-ip-create">Add</button></>}>
        <input className="input" placeholder="IP address" value={form.ip} onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))} data-testid="relay-ip-input" />
        <input className="input" placeholder="Tag (e.g. provider)" value={form.tag} onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))} />
        <input className="input" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
      </Modal>
    </div>
  );
}

function LeadsLog() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 50;
  useEffect(() => { setLoading(true); api.get("/relay/leads", { params: { page, page_size: pageSize } }).then((r) => setData(r.data)).finally(() => setLoading(false)); }, [page]);
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="relay-log-table">
          <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Source</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Received</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-10 text-center"><Spinner className="w-5 h-5 inline text-slate-400" /></td></tr> :
              data.items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800/50">
                  <td className="p-3 font-medium text-slate-800 dark:text-slate-100">{r.name}</td>
                  <td className="p-3 text-slate-600 dark:text-slate-300 max-w-[200px] truncate">{r.email || "—"}</td>
                  <td className="p-3 text-slate-600 dark:text-slate-300">{r.phone || "—"}</td>
                  <td className="p-3 text-slate-600 dark:text-slate-300">{r.payload?.funnel || r.source || "—"}</td>
                  <td className="p-3"><span className="badge bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"><CheckCircle2 className="w-3 h-3" /> {r.status}</span></td>
                  <td className="p-3 text-xs text-slate-400">{r.created_at ? format(new Date(r.created_at), "MMM d, h:mm a") : ""}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 border-t border-slate-100 dark:border-slate-800"><Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} /></div>
    </div>
  );
}

function TestTab() {
  const [form, setForm] = useState({ api_key: "", name: "", email: "", phone: "", source: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const send = async () => {
    setBusy(true); setRes(null);
    try { const r = await api.post("/relay/test", { full_name: form.name, email: form.email, phone: form.phone, source: form.source }); setRes(r.data.message); toast("Test lead sent"); }
    catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <div className="card p-6 max-w-xl">
      <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-1">Send Test Lead</h3>
      <p className="text-xs text-slate-400 mb-4">Paste the full key you copied when it was created. Only a hash is stored server-side.</p>
      <div className="space-y-3">
        <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">API Key</label><input className="input mt-1 font-mono" value={form.api_key} onChange={(e) => upd("api_key", e.target.value)} data-testid="relay-test-key" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Name</label><input className="input mt-1" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="relay-test-name" /></div>
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label><input className="input mt-1" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="relay-test-email" /></div>
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Phone</label><input className="input mt-1" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="relay-test-phone" /></div>
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Source</label><input className="input mt-1" value={form.source} onChange={(e) => upd("source", e.target.value)} data-testid="relay-test-source" /></div>
        </div>
        <button className="btn-primary w-full" onClick={send} disabled={busy} data-testid="relay-test-send">{busy ? <Spinner className="w-4 h-4" /> : <><Send className="w-4 h-4" /> Send Test Lead</>}</button>
        {res && <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {res}</div>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/relay/settings").then((r) => setS(r.data)); }, []);
  const save = async (patch) => { const r = await api.put("/relay/settings", patch); setS(r.data); toast("Saved"); };
  if (!s) return <Spinner className="w-5 h-5 text-slate-400" />;
  const Toggle = ({ field, label, desc }) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-800 p-4">
      <div><div className="font-medium text-slate-800 dark:text-slate-100">{label}</div><div className="text-xs text-slate-400">{desc}</div></div>
      <button onClick={() => save({ [field]: !s[field] })} data-testid={`relay-toggle-${field}`}
        className={`relative h-6 w-11 rounded-full transition-colors ${s[field] ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-700"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${s[field] ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
  return (
    <div className="card p-6 max-w-2xl space-y-4">
      <h3 className="font-semibold font-display text-slate-900 dark:text-white">Settings</h3>
      <div>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">CRM Forward URL</label>
        <input className="input mt-1" defaultValue={s.forward_url} onBlur={(e) => save({ forward_url: e.target.value })} placeholder="https://your-crm.com/intake" data-testid="relay-forward-url" />
        <div className="text-xs text-slate-400 mt-1">Auto-saved</div>
      </div>
      <Toggle field="enforce_ip" label="Enforce IP Whitelist" desc="Reject requests from non-whitelisted IPs." />
      <Toggle field="enforce_key" label="Enforce API Key" desc="Require a valid API key for every request." />
    </div>
  );
}

function Integration() {
  const [ips, setIps] = useState([]);
  const [keys, setKeys] = useState([]);
  useEffect(() => { api.get("/relay/ips").then((r) => setIps(r.data)); api.get("/relay/api-keys").then((r) => setKeys(r.data)); }, []);
  const copy = (t) => { navigator.clipboard.writeText(t); toast("Copied"); };
  const guide = `POST ${ENDPOINT}\nHeaders:\n  X-API-Key: <your_api_key>\n  Content-Type: application/json\nBody:\n  {"full_name":"John Doe","email":"john@x.com","phone":"447400000000","country":"GB","funnel":"YourFunnel"}`;
  return (
    <div className="space-y-6 max-w-4xl">
      <section className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-1">1. Endpoint URL</h3>
        <p className="text-xs text-slate-400 mb-3">Public endpoint — share this with your provider</p>
        <div className="flex gap-2"><code className="flex-1 font-mono text-sm bg-slate-50 dark:bg-slate-800/40 rounded-md px-3 py-2 truncate">{ENDPOINT}</code><button className="btn-secondary" onClick={() => copy(ENDPOINT)} data-testid="relay-copy-endpoint"><Copy className="w-4 h-4" /></button></div>
      </section>
      <section className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-3">2. Active API Keys</h3>
        {keys.filter((k) => k.status === "active").map((k) => (
          <div key={k.id} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 py-2 text-sm"><span className="font-medium text-slate-700 dark:text-slate-200">{k.label}</span><span className="font-mono text-slate-400">{k.key_preview}••••••••</span></div>
        ))}
      </section>
      <section className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-3">3. Whitelisted IPs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ips.map((i) => <div key={i.id} className="flex items-center gap-2 text-sm"><span className="font-mono text-slate-700 dark:text-slate-200">{i.ip}</span><span className="badge bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-800">{i.tag}</span></div>)}
        </div>
      </section>
      <section className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-3">4. Ready-to-send integration guide</h3>
        <pre className="font-mono text-xs bg-slate-50 dark:bg-slate-800/40 rounded-lg p-4 overflow-x-auto text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{guide}</pre>
        <button className="btn-secondary mt-3" onClick={() => copy(guide)} data-testid="relay-copy-guide"><Copy className="w-4 h-4" /> Copy full guide</button>
      </section>
      <section className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-2">5. Lead Status API (same endpoint, GET method)</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">Providers use the same URL and API key to poll lead status (conversion + deposits). POST submits a lead; GET reads status, scoped to the calling key.</p>
      </section>
    </div>
  );
}
